import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../context/StoreContext';
import { reportsAPI, accountingExportsAPI, extractErrorMessage } from '../api';

const TABS = ['sales', 'inventory', 'customers', 'tax', 'staff'];
const PERIODS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This Week' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_year', label: 'This Year' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'last_90_days', label: 'Last 90 Days' },
  { value: 'custom', label: 'Custom Range' },
];

const Reports = () => {
  const { selectedStore } = useStore();
  const [activeTab, setActiveTab] = useState('sales');
  const [period, setPeriod] = useState('this_month');
  const [customDates, setCustomDates] = useState({ start_date: '', end_date: '' });
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper functions
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: selectedStore?.currency || 'USD',
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value || 0);
  };

  const dollar = (v) => v != null ? `$${parseFloat(v).toFixed(2)}` : '-';
  const num = (v) => v != null ? Number(v).toLocaleString() : '-';

  const getParams = useCallback(() => {
    const params = { period };
    if (period === 'custom') {
      if (customDates.start_date) params.start_date = customDates.start_date;
      if (customDates.end_date) params.end_date = customDates.end_date;
    }
    return params;
  }, [period, customDates]);

  const loadData = useCallback(async () => {
    if (!selectedStore) return;
    setLoading(true);
    setError(null);
    const params = getParams();
    try {
      const results = {};
      if (activeTab === 'sales') {
        const [summary, byProduct, byCategory, byPayment] = await Promise.allSettled([
          reportsAPI.salesSummary(selectedStore.id, params),
          reportsAPI.salesByProduct(selectedStore.id, params),
          reportsAPI.salesByCategory(selectedStore.id, params),
          reportsAPI.salesByPaymentMethod(selectedStore.id, params),
        ]);
        results.summary = summary.status === 'fulfilled' ? summary.value.data : null;
        results.byProduct = byProduct.status === 'fulfilled' ? (Array.isArray(byProduct.value.data) ? byProduct.value.data : byProduct.value.data.items || []) : [];
        results.byCategory = byCategory.status === 'fulfilled' ? (Array.isArray(byCategory.value.data) ? byCategory.value.data : byCategory.value.data.items || []) : [];
        results.byPayment = byPayment.status === 'fulfilled' ? (Array.isArray(byPayment.value.data) ? byPayment.value.data : byPayment.value.data.items || []) : [];
      } else if (activeTab === 'inventory') {
        const [summary, aging, turnover, valuation] = await Promise.allSettled([
          reportsAPI.inventorySummary(selectedStore.id),
          reportsAPI.inventoryAging(selectedStore.id),
          reportsAPI.inventoryTurnover(selectedStore.id),
          reportsAPI.inventoryValuation(selectedStore.id),
        ]);
        results.summary = summary.status === 'fulfilled' ? summary.value.data : null;
        results.aging = aging.status === 'fulfilled' ? (Array.isArray(aging.value.data) ? aging.value.data : aging.value.data.items || []) : [];
        results.turnover = turnover.status === 'fulfilled' ? (Array.isArray(turnover.value.data) ? turnover.value.data : turnover.value.data.items || []) : [];
        results.valuation = valuation.status === 'fulfilled' ? (Array.isArray(valuation.value.data) ? valuation.value.data : valuation.value.data.items || []) : [];
      } else if (activeTab === 'customers') {
        const [summary, top] = await Promise.allSettled([
          reportsAPI.customerSummary(selectedStore.id, params),
          reportsAPI.topCustomers(selectedStore.id, params),
        ]);
        results.summary = summary.status === 'fulfilled' ? summary.value.data : null;
        results.topCustomers = top.status === 'fulfilled' ? (Array.isArray(top.value.data) ? top.value.data : top.value.data.items || []) : [];
      } else if (activeTab === 'tax') {
        const [summary, byRate] = await Promise.allSettled([
          reportsAPI.taxSummary(selectedStore.id, params),
          reportsAPI.taxByRate(selectedStore.id, params),
        ]);
        results.summary = summary.status === 'fulfilled' ? summary.value.data : null;
        results.byRate = byRate.status === 'fulfilled' ? (Array.isArray(byRate.value.data) ? byRate.value.data : byRate.value.data.items || []) : [];
      } else if (activeTab === 'staff') {
        const res = await reportsAPI.staffPerformance(selectedStore.id, params).catch(() => ({ data: [] }));
        results.performance = Array.isArray(res.data) ? res.data : res.data.items || [];
      }
      setData(results);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load report'));
    } finally {
      setLoading(false);
    }
  }, [selectedStore, activeTab, getParams]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExport = async (type) => {
    try {
      const params = getParams();
      const response = type === 'sales'
        ? await reportsAPI.exportSales(selectedStore.id, params)
        : await reportsAPI.exportInventory(selectedStore.id, params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}-report.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(extractErrorMessage(err, 'Export failed'));
    }
  };

  const handleAccountingExport = async (type) => {
    try {
      const dateParams = {};
      if (period === 'custom') {
        if (customDates.start_date) dateParams.start_date = customDates.start_date;
        if (customDates.end_date) dateParams.end_date = customDates.end_date;
      }
      let response;
      if (type === 'sales') response = await accountingExportsAPI.downloadSales(selectedStore.id, dateParams);
      else if (type === 'inventory') response = await accountingExportsAPI.downloadInventory(selectedStore.id);
      else response = await accountingExportsAPI.downloadExpenses(selectedStore.id, dateParams);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `accounting-${type}-export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(extractErrorMessage(err, 'Accounting export failed'));
    }
  };

  const renderSummaryKpis = (summary) => {
    if (!summary) return null;
    const entries = Object.entries(summary).filter(([k]) => !k.startsWith('_') && typeof summary[k] !== 'object');
    return (
      <div className="page-stats-row" style={{ marginBottom: '2rem' }}>
        {entries.slice(0, 4).map(([key, value]) => (
          <div className="page-stat-card" key={key}>
            <div className="page-stat-content">
              <div className="page-stat-label">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
              <div className="page-stat-value" style={{ fontSize: '1.75rem' }}>
                {typeof value === 'number'
                  ? (key.includes('amount') || key.includes('revenue') || key.includes('total') || key.includes('value') || key.includes('cost')
                    ? formatCurrency(value)
                    : formatNumber(value))
                  : String(value)}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderTable = (items, columns, title) => {
    if (!items || items.length === 0) {
      return (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header">
            <h3 className="card-title">{title}</h3>
          </div>
          <div className="card-body">
            <div className="page-empty-state" style={{ minHeight: '200px', padding: '2rem' }}>
              <div className="page-empty-title" style={{ fontSize: '1rem' }}>No data available</div>
              <p className="page-empty-text">No data for the selected period</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <h3 className="card-title">{title}</h3>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <div className="table-container">
            <table>
              <thead>
                <tr>{columns.map(col => <th key={col.key}>{col.label}</th>)}</tr>
              </thead>
              <tbody>
                {items.map((item, i) => (
                  <tr key={i}>
                    {columns.map(col => (
                      <td key={col.key} className={col.bold ? 'font-bold' : ''}>
                        {col.format ? col.format(item[col.key], item) : (item[col.key] ?? '-')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="page-modern">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Reports & Analytics</h1>
          <p className="page-subtitle">Comprehensive insights into your business performance</p>
        </div>
        <div className="page-header-actions">
          {activeTab === 'sales' && (
            <>
              <button className="btn btn-secondary" onClick={() => handleExport('sales')}>
                Export Sales
              </button>
              <button className="btn btn-secondary" onClick={() => handleAccountingExport('sales')} title="QuickBooks / Tally / Zoho compatible">
                Accounting CSV
              </button>
            </>
          )}
          {activeTab === 'inventory' && (
            <>
              <button className="btn btn-secondary" onClick={() => handleExport('inventory')}>
                Export Inventory
              </button>
              <button className="btn btn-secondary" onClick={() => handleAccountingExport('inventory')} title="Full stock valuation CSV">
                Accounting CSV
              </button>
            </>
          )}
          {activeTab === 'customers' && (
            <button className="btn btn-secondary" onClick={() => handleAccountingExport('expenses')} title="Expenses CSV for accounting">
              Export Expenses
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Tab Navigation */}
      <div className="tab-bar" style={{ marginBottom: '1.5rem' }}>
        {TABS.map(tab => (
          <button
            key={tab}
            className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Period Filter */}
      <div className="card page-filter-card">
        <div className="page-filter-grid">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Report Period</label>
            <select className="form-select" value={period} onChange={(e) => setPeriod(e.target.value)}>
              {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          {period === 'custom' && (
            <>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={customDates.start_date}
                  onChange={(e) => setCustomDates({ ...customDates, start_date: e.target.value })}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={customDates.end_date}
                  onChange={(e) => setCustomDates({ ...customDates, end_date: e.target.value })}
                />
              </div>
            </>
          )}
          <button className="btn btn-secondary" onClick={loadData} style={{ alignSelf: 'flex-end' }}>
            Refresh Data
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="loading"><div className="spinner"></div><span>Loading report...</span></div>
      ) : (
        <>
          {/* Sales Tab */}
          {activeTab === 'sales' && (
            <>
              {renderSummaryKpis(data.summary)}
              {renderTable(data.byProduct, [
                { key: 'product_name', label: 'Product' },
                { key: 'quantity', label: 'Quantity Sold', format: num },
                { key: 'total_amount', label: 'Revenue', format: dollar, bold: true },
              ], 'Sales by Product')}
              {renderTable(data.byCategory, [
                { key: 'category_name', label: 'Category' },
                { key: 'count', label: 'Count', format: num },
                { key: 'total_amount', label: 'Revenue', format: dollar, bold: true },
              ], 'Sales by Category')}
              {renderTable(data.byPayment, [
                { key: 'payment_method', label: 'Payment Method' },
                { key: 'count', label: 'Transactions', format: num },
                { key: 'total_amount', label: 'Amount', format: dollar, bold: true },
              ], 'Sales by Payment Method')}
            </>
          )}

          {/* Inventory Tab */}
          {activeTab === 'inventory' && (
            <>
              {renderSummaryKpis(data.summary)}
              {renderTable(data.aging, [
                { key: 'product_name', label: 'Product' },
                { key: 'days_in_stock', label: 'Days in Stock', format: num },
                { key: 'quantity', label: 'Quantity', format: num },
              ], 'Inventory Aging')}
              {renderTable(data.turnover, [
                { key: 'product_name', label: 'Product' },
                { key: 'turnover_rate', label: 'Turnover Rate' },
                { key: 'total_sold', label: 'Total Sold', format: num },
              ], 'Inventory Turnover')}
              {renderTable(data.valuation, [
                { key: 'product_name', label: 'Product' },
                { key: 'quantity', label: 'Quantity', format: num },
                { key: 'unit_cost', label: 'Unit Cost', format: dollar },
                { key: 'total_value', label: 'Total Value', format: dollar, bold: true },
              ], 'Inventory Valuation')}
            </>
          )}

          {/* Customers Tab */}
          {activeTab === 'customers' && (
            <>
              {renderSummaryKpis(data.summary)}
              {renderTable(data.topCustomers, [
                { key: 'customer_name', label: 'Customer Name' },
                { key: 'total_orders', label: 'Total Orders', format: num },
                { key: 'total_spent', label: 'Total Spent', format: dollar, bold: true },
              ], 'Top Customers')}
            </>
          )}

          {/* Tax Tab */}
          {activeTab === 'tax' && (
            <>
              {renderSummaryKpis(data.summary)}
              {renderTable(data.byRate, [
                { key: 'tax_rate', label: 'Tax Rate', format: (v) => v != null ? `${v}%` : '-' },
                { key: 'count', label: 'Transactions', format: num },
                { key: 'tax_amount', label: 'Tax Amount', format: dollar, bold: true },
              ], 'Tax by Rate')}
            </>
          )}

          {/* Staff Tab */}
          {activeTab === 'staff' && (
            <>
              {renderTable(data.performance, [
                { key: 'staff_name', label: 'Staff Member' },
                { key: 'total_sales', label: 'Total Sales', format: num },
                { key: 'total_revenue', label: 'Revenue', format: dollar, bold: true },
                { key: 'avg_order_value', label: 'Avg Order Value', format: dollar },
              ], 'Staff Performance')}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Reports;
